import { action } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { ApiError, apiRequest } from '@root/shared/api'
import { tradeApiClientAtom } from '@root/shared/api/trade-api-client-atom'

import type {
  BasicBuyCreatePayload,
  BasicSellCreatePayload,
  BasicSellQuoteCreatePayload,
  BasicWithdrawCreatePayload,
  TradingMode,
} from '../model'

/**
 * Basic, over the wire.
 *
 * None of these calls names a person: the balance, the positions and the
 * orders belong to whoever is signed in, and the server decides who that is.
 * There is nothing here to pass the wrong id to.
 */

export const fetchBasicModeRequestAction = action(async (ctx) => {
  const { tradeApiClient } = ctx.get(tradeApiClientAtom)

  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.basic.basicModeList, []),
  )

  return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
}, 'fetchBasicModeRequestAction')

export const setBasicModeRequestAction = action(
  async (ctx, tradingMode: TradingMode) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.basic.basicModeCreate, [{ tradingMode }]),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'setBasicModeRequestAction',
)

export const fetchBasicBalanceRequestAction = action(async (ctx) => {
  const { tradeApiClient } = ctx.get(tradeApiClientAtom)

  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.basic.basicBalanceList, []),
  )

  return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
}, 'fetchBasicBalanceRequestAction')

export const fetchBasicPositionsRequestAction = action(async (ctx) => {
  const { tradeApiClient } = ctx.get(tradeApiClientAtom)

  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.basic.basicPositionsList, []),
  )

  return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
}, 'fetchBasicPositionsRequestAction')

export const fetchBasicDepositAddressesRequestAction = action(async (ctx) => {
  const { tradeApiClient } = ctx.get(tradeApiClientAtom)

  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.basic.basicDepositAddressesList, []),
  )

  return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
}, 'fetchBasicDepositAddressesRequestAction')

export const buyBasicRequestAction = action(
  async (ctx, payload: BasicBuyCreatePayload) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    // Accepted, not filled. What comes back is an order id; the position
    // appears when the executor has actually bought something.
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.basic.basicBuyCreate, [payload]),
    )

    // The API error as it came, code and all: a refusal names its reason
    // (not enough balance, no deposit address) and the screen says it.
    return result
  },
  'buyBasicRequestAction',
)

export const quoteBasicSellRequestAction = action(
  async (ctx, payload: BasicSellQuoteCreatePayload) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.basic.basicSellQuoteCreate, [payload]),
    )

    // The API error as it came, code and all: a refusal names its reason
    // (not enough balance, no deposit address) and the screen says it.
    return result
  },
  'quoteBasicSellRequestAction',
)

export const sellBasicRequestAction = action(
  async (ctx, payload: BasicSellCreatePayload) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.basic.basicSellCreate, [payload]),
    )

    // The API error as it came, code and all: a refusal names its reason
    // (not enough balance, no deposit address) and the screen says it.
    return result
  },
  'sellBasicRequestAction',
)

export const withdrawBasicRequestAction = action(
  async (ctx, payload: BasicWithdrawCreatePayload) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.basic.basicWithdrawCreate, [payload]),
    )

    // The API error as it came, code and all: a refusal names its reason
    // (not enough balance, no deposit address) and the screen says it.
    return result
  },
  'withdrawBasicRequestAction',
)
